import React, {useState, useEffect} from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	FormControlLabel,
	Switch,
	Typography,
	Box,
	Grid,
	Paper
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import API from "../../../../network/dashboardAPI";
import UploadService from "../../../../services/FileUploadService";
import useAlert from "../../../../services/useAlert";

const useStyles = makeStyles((theme) => ({
	contentPaper: {
		padding: theme.spacing(3),
		backgroundColor: "#f9fafc"
	},
	field: {
		marginBottom: theme.spacing(2)
	},
	uploadButton: {
		marginTop: theme.spacing(1)
	},
	imagePreview: {
		width: 380,
		height: 380,
		borderRadius: 12,
		objectFit: "cover",
		marginTop: theme.spacing(1),
		border: "1px solid #ddd"
	},
	actions: {
		padding: theme.spacing(2)
	},
	saveButton: {
		minWidth: 120
	}
}));

export default function DietaryPreferenceDialog({open, onClose, onSaved, initialData}) {
	const classes = useStyles();
	const alert = useAlert();

	const [name, setName] = useState("");
	const [nameKh, setNameKh] = useState("");
	const [description, setDescription] = useState("");
	const [descriptionKh, setDescriptionKh] = useState("");
	const [isAvailable, setIsAvailable] = useState(true);
	const [imageFile, setImageFile] = useState(null);
	const [imagePreview, setImagePreview] = useState("");
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (initialData) {
			setName(initialData.name || "");
			setNameKh(initialData.name_kh || "");
			setDescription(initialData.description || "");
			setDescriptionKh(initialData.description_kh || "");
			setIsAvailable(initialData.is_available || false);
			setImagePreview(initialData.image_url || "");
			setImageFile(null);
		} else {
			setName("");
			setNameKh("");
			setDescription("");
			setDescriptionKh("");
			setIsAvailable(true);
			setImageFile(null);
			setImagePreview("");
		}
		setErrors({});
	}, [initialData, open]);

	const handleImageChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			const formData = new FormData();
			formData.append("image", file);

			UploadService.uploadImageFromDashboard(formData).then((response) => {
				if (response.data.success) {
					setImageFile("image_url", response.data.data[0].url);
					setImagePreview(response.data.data[0].url);
				}
			});
		}
	};

	const validate = () => {
		const newErrors = {};
		if (!name) newErrors.name = "Name (EN) is required";
		if (!description) newErrors.description = "Description (EN) is required";
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSave = async () => {
		if (!validate()) return;

		try {
			setLoading(true);

			const payload = {
				name,
				name_kh: nameKh,
				description,
				description_kh: descriptionKh,
				is_available: isAvailable,
				image_url: imagePreview // use URL or base64 string
			};

			let response;
			const isEdit = !!initialData?.diet_id;

			if (isEdit) {
				// Update
				response = await API.updateDietaryPreference(initialData.diet_id, payload);
			} else {
				// Add
				response = await API.addDietaryPreference(payload);
			}

			const res = await response.json();

			// Close loading alert
			alert.close();

			if (res.success) {
				const action = isEdit ? "edit" : "add";
				const itemName = name || initialData?.name;

				// Show success message
				alert.success(
					`${isEdit ? "Updated Successfully!" : "Created Successfully!"}`,
					`"${itemName}" has been ${isEdit ? "updated" : "created"} successfully.`
				);

				// Show toast notification
				alert.toast(
					`Dietary preference ${isEdit ? "updated" : "created"}`,
					'success'
				);

				// Call parent callback with action and item name
				onSaved(action, itemName);
				onClose();
			} else {
				// Handle API error
				const errorMessage = res.message || `Failed to ${isEdit ? "update" : "create"} dietary preference`;
				console.error(errorMessage);

				alert.error(
					`${isEdit ? "Update Failed" : "Creation Failed"}`,
					errorMessage
				);
			}
		} catch (err) {
			console.error("Error saving dietary preference:", err);

			// Close any open loading alert
			alert.close();

			// Handle network error
			alert.error(
				"Network Error",
				`Unable to ${initialData?.diet_id ? "update" : "create"} dietary preference. Please check your connection and try again.`
			);
		} finally {
			setLoading(false);
		}
	};


	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				{initialData ? "Edit Dietary Preference" : "Add Dietary Preference"}
			</DialogTitle>
			<DialogContent>
				<Paper className={classes.contentPaper}>
					<Grid container spacing={3}>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Name (EN)"
								fullWidth
								variant="outlined"
								className={classes.field}
								value={name}
								onChange={(e) => setName(e.target.value)}
								error={!!errors.name}
								helperText={errors.name}
							/>
							<TextField
								label="Name (KH)"
								fullWidth
								variant="outlined"
								className={classes.field}
								value={nameKh}
								onChange={(e) => setNameKh(e.target.value)}
							/>
							<TextField
								label="Description (EN)"
								fullWidth
								variant="outlined"
								multiline
								rows={4}
								className={classes.field}
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								error={!!errors.description}
								helperText={errors.description}
							/>
							<TextField
								label="Description (KH)"
								fullWidth
								variant="outlined"
								multiline
								rows={4}
								className={classes.field}
								value={descriptionKh}
								onChange={(e) => setDescriptionKh(e.target.value)}
							/>
							<FormControlLabel
								control={
									<Switch
										checked={isAvailable}
										onChange={(e) => setIsAvailable(e.target.checked)}
										color="primary"
									/>
								}
								label="Is Available"
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Box display="flex" flexDirection="column" alignItems="center" mt={2}>
								<input
									accept="image/*"
									type="file"
									id="dietary-image-upload"
									style={{display: "none"}}
									onChange={handleImageChange}
								/>
								<label htmlFor="dietary-image-upload" style={{width: "100%"}}>
									<Button
										variant="outlined"
										color="primary"
										component="span"
										fullWidth
										style={{
											borderStyle: "dashed",
											borderWidth: 2,
											padding: "20px",
											textTransform: "none",
											borderRadius: 12,
										}}
									>
										{imagePreview ? "Change Image" : "Click to Upload Image"}
									</Button>
								</label>

								{imagePreview && (
									<Box
										mt={2}
										p={1.5}
										border="1px solid #e0e0e0"
										borderRadius={12}
										boxShadow={1}
										display="flex"
										flexDirection="column"
										alignItems="center"
										maxWidth={250}
										width="100%"
									>
										<img
											src={imagePreview}
											alt="Preview"
											style={{
												width: "100%",
												height: "auto",
												borderRadius: 8,
												objectFit: "cover",
											}}
										/>
										<Typography
											variant="caption"
											color="textSecondary"
											style={{marginTop: 8}}
										>
											Preview
										</Typography>
									</Box>
								)}
							</Box>

						</Grid>
					</Grid>
				</Paper>
			</DialogContent>
			<DialogActions className={classes.actions}>
				<Button onClick={onClose} color="default" variant="outlined" disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={handleSave}
					color="primary"
					variant="contained"
					className={classes.saveButton}
					disabled={loading}
				>
					{loading ? "Saving..." : "Save"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
